import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const ResultsScreen = ({ 
  score, 
  totalQuestions, 
  testTitle,
  answers,
  questions,
  onRetake,
  onBackToTests 
}) => {
  const navigate = useNavigate();
  const percentage = Math.round((score / totalQuestions) * 100);
  const passingScore = Math.ceil(totalQuestions * 0.75);
  const isPassed = score >= passingScore;
  const [showLowScorePopup, setShowLowScorePopup] = useState(percentage < 80);

  const getScoreColor = () => {
    if (percentage >= 85) return '#4CAF50'; // Green
    if (percentage >= 75) return '#2196F3'; // Blue
    if (percentage >= 60) return '#FF9800'; // Orange
    return '#F44336'; // Red
  };

  const getScoreMessage = () => {
    if (percentage >= 90) return 'Excellent Performance!';
    if (percentage >= 83) return 'Pass - Well Done!';
    if (percentage >= 60) return 'Close! Review the material.';
    return 'Need More Practice';
  };

  const getCategoryBreakdown = () => {
    const categories = {};
    questions.forEach((q, idx) => {
      const category = q.category || 'General';
      if (!categories[category]) {
        categories[category] = { correct: 0, total: 0 };
      }
      categories[category].total += 1;
      if (answers[idx] === q.answerIndex) {
        categories[category].correct += 1;
      }
    });
    return categories;
  };

  const categoryBreakdown = getCategoryBreakdown();

  const handleUpgradeNow = () => {
    setShowLowScorePopup(false);
    navigate('/checkout', { state: { from: 'mock-test-low-score' } });
  };

  return (
    <>
      {showLowScorePopup && (
        <div className="low-score-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="low-score-title">
          <div className="low-score-modal-card">
            <button
              type="button"
              className="low-score-modal-close"
              aria-label="Close recommendation popup"
              onClick={() => setShowLowScorePopup(false)}
            >
              x
            </button>
            <h3 id="low-score-title">Boost Your Pass Chance Before Real Exam</h3>
            <p>
              Your score is below <strong>80%</strong>, which puts you at high risk of failing the real exam.
              Retaking the official test can cost about <strong>30 CAD</strong> and waste your valuable time.
            </p>
            <p>
              Upgrade to <strong>Premium for only 1.99 CAD</strong> to save money, save time, and get stronger support
              from driving experts with detailed explanations.
            </p>
            <p className="low-score-payment-note">
              Secure payment supported via <strong>PayPal</strong> and <strong>debit/credit card</strong>.
            </p>
            <div className="low-score-actions">
              <button type="button" className="btn btn-primary" onClick={handleUpgradeNow}>
                Upgrade for 1.99 CAD
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowLowScorePopup(false)}
              >
                Continue Free
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="results-screen">
        <div className={`results-header ${isPassed ? 'passed' : 'failed'}`}>
        <h1>{testTitle}</h1>
        <div className="score-display">
          <div className="score-circle" style={{ borderColor: getScoreColor() }}>
            <div className="score-content">
              <span className="score-number">{score}/{totalQuestions}</span>
              <span className="score-percentage">{percentage}%</span>
            </div>
          </div>
          <div className="score-message">
            <h2 className={isPassed ? 'pass' : 'fail'}>
              {isPassed ? '✓ PASSED' : '✗ FAILED'}
            </h2>
            <p>{getScoreMessage()}</p>
          </div>
        </div>

        {!isPassed && (
          <div className="pass-requirement">
            <p>You need <strong>{passingScore}</strong> questions correct to pass (75%)</p>
            <p>You got <strong>{score}</strong> correct - <strong>{passingScore - score}</strong> more needed</p>
          </div>
        )}
        </div>

        <div className="results-details">
        <div className="category-breakdown">
          <h3>Performance by Category</h3>
          <div className="category-list">
            {Object.entries(categoryBreakdown).map(([category, data]) => (
              <div key={category} className="category-item">
                <span className="category-name">{category}</span>
                <div className="category-progress">
                  <div 
                    className="category-bar"
                    style={{ width: `${(data.correct / data.total) * 100}%` }}
                  ></div>
                </div>
                <span className="category-score">{data.correct}/{data.total}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="summary-stats">
          <h3>Summary</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-value correct-count">{score}</div>
              <div className="stat-label">Correct</div>
            </div>
            <div className="stat-item">
              <div className="stat-value incorrect-count">{totalQuestions - score}</div>
              <div className="stat-label">Incorrect</div>
            </div>
            <div className="stat-item">
              <div className="stat-value percentage">{percentage}%</div>
              <div className="stat-label">Score</div>
            </div>
            <div className="stat-item">
              <div className="stat-value passing-score">{passingScore}</div>
              <div className="stat-label">Pass Score</div>
            </div>
          </div>
        </div>
        </div>

        <div className="results-actions">
        <button 
          className="btn btn-primary"
          onClick={onRetake}
        >
          Retake Test
        </button>
        <button 
          className="btn btn-secondary"
          onClick={onBackToTests}
        >
          Back to Tests
        </button>
        </div>
      </div>
    </>
  );
};

export default ResultsScreen;
